import {
    CardCollection,
    DateIO,
    format,
    Instance,
    Knot,
    Objekt,
    Promize,
    Query,
} from '@siposdani87/sui-js';
import { app } from '../app';
import { AssetService } from '@services/assetService';
import { LanguageService } from '@services/languageService';
import { OrganizationService } from '@services/organizationService';
import { PlanService } from '@services/planService';
import { UtilService } from '@services/utilService';
import { initCardCollection } from '@utils/collectionSetup';
import { DashboardController } from './dashboardController';

export class PlansIndexController extends DashboardController {
    static readonly inject = [
        'instances',
        'planService',
        'organizationService',
        'utilService',
        'assetService',
        'languageService',
    ] as const;

    planCardCollection!: CardCollection;

    constructor(
        instances: Instance,
        private planService: PlanService,
        private organizationService: OrganizationService,
        private utilService: UtilService,
        private assetService: AssetService,
        private languageService: LanguageService,
    ) {
        super(instances);
    }

    protected override _initLayout(): void {
        this._initToolbarButtons();
        this._initFilterForm();

        this._drawContent();
    }

    protected override _drawContent(): Promize | boolean {
        this._initPlanCardCollection();
        return true;
    }

    private _initToolbarButtons(): void {
        this.helper.fabButton('.btn-plans-new', this.dom, () => {
            this.planService
                .newDialog('organizations', 'organization_id')
                .then(() => {
                    this._drawContent();
                });
        });

        this.helper.iconButton('.btn-site-home', this.dom, () => {
            this.state.goBack('site.home');
        });
    }

    private _initPlanCardCollection(): void {
        this.planCardCollection = initCardCollection(
            this.planCardCollection,
            this.dom,
            '.plan-collection',
            this.planService,
            {
                entityKey: 'plans',
                fetchData: (params) => this.planService.getAll(params),
                extraParams: (params) => {
                    params.set('query', this.query);
                    params.set('organization_ids', this.organizationIds);
                    params.set(
                        'fields',
                        'id,full_name,start_at,end_at,open_season.name,open_season.country.name,' +
                            this.organizationService.getAssociationTableFields(),
                    );
                },
            },
            this._planCardKnot.bind(this),
            {
                formatStartAtAndEndAt:
                    this.formatStartAtAndEndAt.bind(this),
            },
            { sort: { column: 'start_at', order: 'desc' } },
        )!;
    }

    private _planCardKnot(cardKnot: Knot, plan: Objekt): void {
        this._initPlanActivity(cardKnot, plan);
        this._initPlanShowButton(cardKnot, plan);
        this._initCardButtons(cardKnot, plan);
        this.utilService.initOrganizationLink(cardKnot, plan);
    }

    private _initPlanActivity(cardKnot: Knot, plan: Objekt): void {
        const cardItem = new Query('.card', cardKnot).getKnot();
        const endAt = DateIO.parse(plan.get<string>('end_at'));
        const cssClass = DateIO.isBefore(new Date(), endAt)
            ? 'active'
            : 'inactive';
        cardItem.addClass(cssClass);
    }

    private _initPlanShowButton(cardKnot: Knot, plan: Objekt): void {
        this.helper.link(
            'a.card',
            cardKnot,
            (href) => {
                this.state.go(href);
            },
            this.state.resolveUrl('plans.show', {
                planId: plan.get<string>('id'),
            }),
        );
    }

    private _initCardButtons(cardKnot: Knot, plan: Objekt): void {
        // let actionsKnot = new Query('.actions', cardKnot).getKnot();
    }

    formatStartAtAndEndAt(startAt: string, endAt: string): string {
        const startAtFormatted = this.languageService.format(
            DateIO.parse(startAt),
            'format.date',
        );
        const endAtFormatted = this.languageService.format(
            DateIO.parse(endAt),
            'format.date',
        );
        return format('{0} ‒ {1}', [startAtFormatted, endAtFormatted]);
    }
}

export const plansIndexController = app.controller(
    'plansIndexController',
    PlansIndexController,
);
